// Shared helper for selector-based JS mounts used on Turbo pages.
// In this codebase it is used by Vue entrypoints mounted from existing DOM nodes,
// so it remounts them after Turbo updates and asks the registry-backed destroy
// callback to tear them down before the old DOM is removed.
export default function mountComponent({ selector, create, destroy, mountOnDOMContentLoaded = false }) {
  const forEachMatch = (root, callback) => {
    if (!root) {
      return;
    }

    if (root.matches?.(selector)) {
      callback(root);
    }

    root.querySelectorAll?.(selector).forEach(callback);
  };

  const mountAll = (root = document) => {
    forEachMatch(root, create);
  };

  const destroyAll = (root = document) => {
    destroy(root);
  };

  const destroyStreamTargets = (streamElement) => {
    if (!['remove', 'replace', 'update'].includes(streamElement.action)) {
      return;
    }

    streamElement.targetElements.forEach((element) => destroyAll(element));
  };

  if (mountOnDOMContentLoaded) {
    document.addEventListener('DOMContentLoaded', () => mountAll(document));
  }

  document.addEventListener('turbo:load', () => mountAll(document));
  document.addEventListener('turbo:render', () => mountAll(document));
  document.addEventListener('turbo:before-stream-render', (event) => {
    destroyStreamTargets(event.target);
    const render = event.detail.render;

    const streamElement = event.target;

    event.detail.render = async (...args) => {
      // Turbo dispatches every stream's before-render BEFORE any render runs, and render itself
      // is async. Mounting document-wide here re-mounted placeholders a LATER stream was about to
      // replace, leaving orphaned apps whose listeners outlive the swap (duplicated dialogs).
      // Await the render and mount only within this stream's own targets (targetElements
      // re-resolves by id, so after the render it yields the fresh nodes).
      await render(...args);
      streamElement.targetElements.forEach((element) => mountAll(element));
    };
  });
  document.addEventListener('turbo:frame-load', (event) => mountAll(event.target));
  document.addEventListener('turbo:before-frame-render', (event) => destroyAll(event.target));
  document.addEventListener('turbo:before-cache', () => destroyAll(document));

  mountAll(document);
}
